import { memo, useEffect, useMemo, useRef } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { CalendarX2, WifiOff } from 'lucide-react'

import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { getEffectiveResponse, SHOW_WORKER_RESPONSE_STATUS } from '../schedulingRules'
import type { Job, JobWorker, Worker, WorkerResponse, WorkHours } from '../types'
import { getVisibleJobWorkers, isActiveResponse } from '../utils/job-workers'
import { WORKER_STATUS_LABEL, type JobDisplayStatus } from '../utils/job-status'
import { formatRelativeTime, formatTimeRangeInZone, getMinutesOfDay, toDateKey } from '../utils/time'
import { JOB_STATUS_VISUAL, WORKER_STATUS_VISUAL, WorkerAvatar } from './status-meta'

const NAME_COLUMN_PX = 184
const HOUR_MIN_PX = 88

export interface TimelineRange {
  startMinutes: number
  endMinutes: number
}

/** Work hours, widened to include every job on the day and the current time. */
export function resolveTimelineRange(
  jobs: Job[],
  workHours: WorkHours,
  timeZone: string,
  dateKey: string,
  nowMinutes: number | null,
): TimelineRange {
  let start = workHours.startHour * 60
  let end = workHours.endHour * 60
  jobs.forEach((job) => {
    if (toDateKey(job.scheduledStart, timeZone) !== dateKey) return
    const jobStart = getMinutesOfDay(job.scheduledStart, timeZone)
    const endsSameDay = toDateKey(job.scheduledEnd, timeZone) === dateKey
    const jobEnd = endsSameDay ? getMinutesOfDay(job.scheduledEnd, timeZone) : 24 * 60
    start = Math.min(start, Math.floor(jobStart / 60) * 60)
    end = Math.max(end, Math.ceil(jobEnd / 60) * 60)
  })
  if (nowMinutes != null) {
    start = Math.min(start, Math.floor(nowMinutes / 60) * 60)
    end = Math.max(end, Math.ceil((nowMinutes + 1) / 60) * 60)
  }
  return { startMinutes: Math.max(0, start), endMinutes: Math.min(24 * 60, Math.max(end, start + 60)) }
}

const toPercent = (minutes: number, range: TimelineRange) =>
  ((Math.min(Math.max(minutes, range.startMinutes), range.endMinutes) - range.startMinutes) / (range.endMinutes - range.startMinutes)) * 100

function formatHourLabel(hour: number) {
  const suffix = hour >= 12 && hour < 24 ? 'PM' : 'AM'
  const display = hour % 12 === 0 ? 12 : hour % 12
  return `${display} ${suffix}`
}

/** One job as it appears on one worker's row. */
interface WorkerJobEntry {
  job: Job
  jobWorker: JobWorker
}

/** Response styles, used only with SHOW_WORKER_RESPONSE_STATUS (accepted falls through to the job status). */
const RESPONSE_BLOCK_STYLE: Partial<Record<WorkerResponse, { className: string; label: string }>> = {
  declined: { className: 'border border-red-300 text-red-800 bg-[repeating-linear-gradient(45deg,#fee2e2_0,#fee2e2_6px,#fecaca_6px,#fecaca_12px)]', label: 'Declined' },
  expired: { className: 'border-2 border-dashed border-slate-300 bg-slate-50 text-slate-500', label: 'Expired' },
  pending: { className: 'border-2 border-dashed border-amber-400 bg-amber-50/80 text-amber-900', label: 'Pending' },
}

/** Block style by job status (late red); with the response UI on, pending/declined/expired first. */
function blockStyle(response: WorkerResponse, display: JobDisplayStatus): { className: string; label: string } {
  const responseStyle = SHOW_WORKER_RESPONSE_STATUS ? RESPONSE_BLOCK_STYLE[response] : undefined
  if (responseStyle) return responseStyle
  if (display.kind === 'late') return { className: 'bg-red-600 text-white', label: 'Late' }
  return { className: cn(JOB_STATUS_VISUAL[display.kind].solid, 'text-white', display.kind === 'completed' && 'opacity-70'), label: display.label }
}

interface WorkerRowProps {
  worker: Worker
  jobs: WorkerJobEntry[]
  range: TimelineRange
  timeZone: string
  dateKey: string
  nowMinutes: number | null
  now: number
  displayById: Map<string, JobDisplayStatus>
  selectedJobId: string | null
  onSelectJob: (jobId: string) => void
}

const WorkerRow = memo(function WorkerRow({
  worker, jobs, range, timeZone, dateKey, nowMinutes, now, displayById, selectedJobId, onSelectJob,
}: WorkerRowProps) {
  const { setNodeRef, isOver, active } = useDroppable({ id: `worker:${worker.id}`, data: { workerId: worker.id } })
  const visual = WORKER_STATUS_VISUAL[worker.status]
  const StatusIcon = visual.icon

  const blocks = jobs.map(({ job, jobWorker }) => {
    const startMin = toDateKey(job.scheduledStart, timeZone) === dateKey ? getMinutesOfDay(job.scheduledStart, timeZone) : range.startMinutes
    const endMin = toDateKey(job.scheduledEnd, timeZone) === dateKey ? getMinutesOfDay(job.scheduledEnd, timeZone) : range.endMinutes
    const response = getEffectiveResponse(jobWorker, now)
    return { job, response, startMin, endMin, busy: isActiveResponse(response) }
  })

  // Dashed "Available" window: from now (or day start) to the next job.
  let availableBlock: { startMin: number; endMin: number } | null = null
  if (worker.status === 'available') {
    const from = Math.max(range.startMinutes, nowMinutes ?? range.startMinutes)
    const busyBlocks = blocks.filter((block) => block.busy)
    const nextStart = busyBlocks.filter((block) => block.startMin >= from).reduce((min, block) => Math.min(min, block.startMin), range.endMinutes)
    const busyNow = busyBlocks.some((block) => block.startMin <= from && block.endMin > from)
    if (!busyNow && nextStart - from >= 15) availableBlock = { startMin: from, endMin: nextStart }
  }

  return (
    <div role="row" className="grid border-b border-[#eef3ef] last:border-b-0" style={{ gridTemplateColumns: `${NAME_COLUMN_PX}px 1fr` }}>
      <div role="rowheader" className="sticky left-0 z-10 flex items-center gap-2 border-r border-[#eef3ef] bg-white px-3 py-2">
        <WorkerAvatar name={worker.name} avatarUrl={worker.avatarUrl} status={worker.status} />
        <div className="min-w-0">
          <p className="truncate text-[12px] font-semibold text-[#20352b]">{worker.name}</p>
          <p className="flex items-center gap-1 text-[10px] text-[#718078]">
            <StatusIcon className="h-3 w-3" aria-hidden="true" />
            {WORKER_STATUS_LABEL[worker.status]}
          </p>
        </div>
      </div>

      <div
        ref={setNodeRef}
        role="gridcell"
        aria-label={`${worker.name} schedule${active ? ' — drop here to assign' : ''}`}
        className={cn(
          'relative h-14 transition-colors',
          worker.status === 'offline' && 'bg-slate-50',
          active && 'bg-[#fafdfb]',
          isOver && 'bg-[#e8f4ee] ring-2 ring-inset ring-[#169b62]',
        )}
      >
        {worker.status === 'offline' && blocks.length === 0 ? (
          <div
            className="absolute inset-y-2 flex items-center gap-1.5 rounded-md bg-slate-200/80 px-2 text-[11px] font-medium text-slate-600"
            style={{ left: '0.5%', right: '0.5%' }}
          >
            <WifiOff className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate">Offline · last seen {worker.lastSeenAt ? formatRelativeTime(worker.lastSeenAt, now) : 'unknown'}</span>
          </div>
        ) : null}

        {availableBlock ? (
          <div
            className="absolute inset-y-2 flex items-center rounded-md border border-dashed border-emerald-400 bg-emerald-50/70 px-2 text-[11px] font-medium text-emerald-700"
            style={{
              left: `${toPercent(availableBlock.startMin, range)}%`,
              width: `${toPercent(availableBlock.endMin, range) - toPercent(availableBlock.startMin, range)}%`,
            }}
          >
            <span className="truncate">Available</span>
          </div>
        ) : null}

        {blocks.map(({ job, response, startMin, endMin }) => {
          const display = displayById.get(job.id)
          if (!display) return null
          const style = blockStyle(response, display)
          const left = toPercent(startMin, range)
          const width = Math.max(toPercent(endMin, range) - left, 1.5)
          const timeRange = formatTimeRangeInZone(job.scheduledStart, job.scheduledEnd, timeZone)
          return (
            <Tooltip key={job.id}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  data-job-block={job.id}
                  onClick={() => onSelectJob(job.id)}
                  aria-label={`${job.bookingNo} ${job.service}, ${timeRange}, ${worker.name}: ${style.label}${job.area ? `, ${job.area}` : ''}`}
                  aria-pressed={selectedJobId === job.id}
                  className={cn(
                    'absolute inset-y-2 flex items-center overflow-hidden rounded-md px-2 text-left text-[11px] font-semibold shadow-sm',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#20352b] focus-visible:ring-offset-1',
                    style.className,
                    selectedJobId === job.id && 'z-10 ring-[3px] ring-[#20352b] ring-offset-2 shadow-lg',
                    selectedJobId != null && selectedJobId !== job.id && 'opacity-60',
                  )}
                  style={{ left: `${left}%`, width: `${width}%` }}
                >
                  <span className="truncate">{job.bookingNo} {job.service}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                <p className="font-semibold">{job.bookingNo} · {job.service}</p>
                {job.area ? <p>{job.area}</p> : null}
                <p>{timeRange}</p>
                <p>{display.label}{display.detail ? ` · ${display.detail}` : ''} · {worker.name}: {style.label}</p>
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    </div>
  )
})

export const LiveScheduleTimeline = memo(function LiveScheduleTimeline({
  workers,
  jobs,
  dateKey,
  dateLabel,
  timeZone,
  timeZoneLabel,
  workHours,
  now,
  isToday,
  displayById,
  selectedJobId,
  isLoading,
  emptyTitle,
  emptyDescription,
  onSelectJob,
}: {
  workers: Worker[]
  jobs: Job[]
  dateKey: string
  dateLabel: string
  timeZone: string
  timeZoneLabel: string
  workHours: WorkHours
  now: number
  isToday: boolean
  displayById: Map<string, JobDisplayStatus>
  selectedJobId: string | null
  isLoading: boolean
  emptyTitle: string
  emptyDescription: string
  onSelectJob: (jobId: string) => void
}) {
  const nowMinutes = isToday ? getMinutesOfDay(now, timeZone) : null
  // Bucketed to the hour so the range only changes when the hour does, not every minute.
  const nowHourMinutes = nowMinutes == null ? null : Math.floor(nowMinutes / 60) * 60
  const range = useMemo(
    () => resolveTimelineRange(jobs, workHours, timeZone, dateKey, nowHourMinutes),
    [jobs, workHours, timeZone, dateKey, nowHourMinutes],
  )

  // One block per worker on each job (declined offers too, hatched, with the response UI on).
  const jobsByWorker = useMemo(() => {
    const map = new Map<string, WorkerJobEntry[]>()
    jobs.forEach((job) => {
      getVisibleJobWorkers(job).forEach((jobWorker) => {
        const list = map.get(jobWorker.workerId) ?? []
        list.push({ job, jobWorker })
        map.set(jobWorker.workerId, list)
      })
    })
    return map
  }, [jobs])

  const hours = useMemo(() => {
    const list: number[] = []
    for (let minute = range.startMinutes; minute < range.endMinutes; minute += 60) list.push(minute / 60)
    return list
  }, [range])

  // Bring the selected job's block into view inside the timeline's own horizontal scroller
  // (the page itself is not scrolled, so the map the user is looking at stays put).
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller || !selectedJobId) return
    const block = Array.from(scroller.querySelectorAll<HTMLElement>('[data-job-block]'))
      .find((element) => element.dataset.jobBlock === selectedJobId)
    if (!block) return
    const scrollerRect = scroller.getBoundingClientRect()
    const blockRect = block.getBoundingClientRect()
    const target = scroller.scrollLeft + (blockRect.left - scrollerRect.left) - (scroller.clientWidth - blockRect.width) / 2
    scroller.scrollTo({
      left: Math.max(0, target),
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
  }, [selectedJobId])

  const trackMinWidth = hours.length * HOUR_MIN_PX
  const nowPercent = nowMinutes != null && nowMinutes >= range.startMinutes && nowMinutes <= range.endMinutes ? toPercent(nowMinutes, range) : null

  return (
    <section aria-label="Live schedule" className="min-w-0 overflow-hidden rounded-xl border border-[#dfeae3] bg-white">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[#dfeae3] px-4 py-3">
        <div>
          <h2 className="text-base font-semibold text-[#20352b]">Live schedule</h2>
          <p className="text-xs text-[#718078]">{dateLabel} · {timeZoneLabel} · drag an unassigned job onto a worker to assign</p>
        </div>
        <ul className="flex flex-wrap items-center gap-3 text-[10px] text-[#4e6157]" aria-label="Schedule legend">
          {(['scheduled', 'en_route', 'in_progress', 'late', 'completed'] as const).map((kind) => (
            <li key={kind} className="flex items-center gap-1">
              <span className={cn('h-2.5 w-2.5 rounded-sm', JOB_STATUS_VISUAL[kind].solid)} aria-hidden="true" />
              {kind === 'en_route' ? 'En route' : kind === 'in_progress' ? 'In progress' : kind[0].toUpperCase() + kind.slice(1)}
            </li>
          ))}
          {SHOW_WORKER_RESPONSE_STATUS ? (
            <>
              <li className="flex items-center gap-1">
                <span className="h-2.5 w-3.5 rounded-sm border-2 border-dashed border-amber-400 bg-amber-50" aria-hidden="true" />
                Pending
              </li>
              <li className="flex items-center gap-1">
                <span className="h-2.5 w-3.5 rounded-sm border border-red-300 bg-[repeating-linear-gradient(45deg,#fee2e2_0,#fee2e2_3px,#fecaca_3px,#fecaca_6px)]" aria-hidden="true" />
                Declined
              </li>
            </>
          ) : null}
          <li className="flex items-center gap-1">
            <span className="h-2.5 w-3.5 rounded-sm border border-dashed border-emerald-500 bg-emerald-50" aria-hidden="true" />
            Available
          </li>
        </ul>
      </header>

      {isLoading ? (
        <div className="space-y-2 p-4" aria-busy="true" aria-label="Loading schedule">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      ) : workers.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#e8f4ee] text-[#169b62]">
            <CalendarX2 className="h-5 w-5" aria-hidden="true" />
          </span>
          <p className="text-sm font-semibold text-[#20352b]">{emptyTitle}</p>
          <p className="max-w-sm text-xs text-[#718078]">{emptyDescription}</p>
        </div>
      ) : (
        // inline-size containment keeps the wide grid from widening the app shell; it scrolls here instead.
        <div ref={scrollerRef} className="overflow-x-auto [contain:inline-size]">
          <div role="grid" aria-label={`Worker schedule for ${dateLabel}`} className="relative" style={{ minWidth: NAME_COLUMN_PX + trackMinWidth }}>
            <div role="row" className="sticky top-0 z-20 grid border-b border-[#dfeae3] bg-[#f7fbf8]" style={{ gridTemplateColumns: `${NAME_COLUMN_PX}px 1fr` }}>
              <div role="columnheader" className="sticky left-0 z-10 bg-[#f7fbf8] px-3 py-2 text-[11px] font-semibold text-[#718078]">Worker</div>
              <div className="relative h-8">
                {hours.map((hour, index) => (
                  <span
                    key={hour}
                    role="columnheader"
                    className="absolute top-2 -translate-x-1/2 text-[10px] font-medium text-[#718078] first:translate-x-0"
                    style={{ left: `${(index / hours.length) * 100}%` }}
                  >
                    {formatHourLabel(hour)}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative">
              {/* Hour grid + now line sit behind/over the tracks only. */}
              <div className="pointer-events-none absolute inset-y-0 right-0" style={{ left: NAME_COLUMN_PX }} aria-hidden="true">
                {hours.map((hour, index) => (
                  <span key={hour} className="absolute inset-y-0 border-l border-[#eef3ef]" style={{ left: `${(index / hours.length) * 100}%` }} />
                ))}
              </div>

              {workers.map((worker) => (
                <WorkerRow
                  key={worker.id}
                  worker={worker}
                  jobs={jobsByWorker.get(worker.id) ?? EMPTY_JOBS}
                  range={range}
                  timeZone={timeZone}
                  dateKey={dateKey}
                  nowMinutes={nowMinutes}
                  now={now}
                  displayById={displayById}
                  selectedJobId={selectedJobId}
                  onSelectJob={onSelectJob}
                />
              ))}

              {nowPercent != null ? (
                <div className="pointer-events-none absolute inset-y-0 right-0 z-10" style={{ left: NAME_COLUMN_PX }} aria-hidden="true">
                  <span className="absolute inset-y-0 w-0.5 bg-red-500" style={{ left: `${nowPercent}%` }}>
                    <span className="absolute -left-[3px] -top-1 h-2 w-2 rounded-full bg-red-500" />
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </section>
  )
})

const EMPTY_JOBS: WorkerJobEntry[] = []
